// ============================================================
// CombatLogPanel - the centre panel: the order strip above the map, the
// prompts that stop an attack to ask a question lying over its top edge,
// and the combat log over its foot with the bottom bar's four on its edge.
//
// The pieces live beside it: the one-question bars in CombatPrompts,
// the reaction prompt and its state in DefencePrompt, the map and the
// log in CombatLogOverlay. This file decides which prompt is up.
// ============================================================
import { memo, useEffect } from 'react';
import clsx from '../utils/clsx';
import common from '../styles/common.module.css';
import styles from '../styles/CombatLogPanel.module.css';
import { resolveLogView } from '../hooks/useLogOverlay';
import { useTargets } from '../contexts/TargetsContext';
import { useSession } from '../contexts/SessionContext';
import { useCombatFlow } from '../contexts/CombatFlowContext';
import { useChatContext } from '../contexts/ChatContext';
import { useRosterContext } from '../contexts/RosterContext';
import {
  AwaitingBar,
  BonusAttackPrompt,
  DefenceChainBar,
  OverheatPrompt,
  promptOwner,
  RerollPrompt,
  TurnEffectPrompt,
} from './CombatPrompts';
import DefencePrompt, { defenceKeyOf, deriveDefence, useDefenceRulings, useStandingAuto } from './DefencePrompt';
import CombatLogOverlay from './CombatLogOverlay';

/** The unified pendingDefence if there is one, else the legacy pendingAttack aimed at this tab. */
function currentDefence(flow, activeTab) {
  const { pendingDefence, pendingAttack } = flow;
  return pendingDefence || (pendingAttack && pendingAttack.tabId === activeTab ? pendingAttack : null);
}

/**
 * What you can see, you have read (v8.363.5): the open shared log shows
 * every line addressed to you, whichever chip is active.
 */
function useVoxReadOnView(vox, collapsed, view) {
  const voxNewest = vox?.messages.length ? vox.messages[vox.messages.length - 1].ts : 0;
  const voxUnread = vox?.unread.total ?? 0;
  useEffect(() => {
    if (!vox || collapsed || view !== 'shared' || voxUnread === 0) return;
    vox.markReadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed, view, voxNewest, voxUnread]);
}

/**
 * Whether this client counts the standing reaction down. Only the client
 * the prompt is LOUD for counts down (or a solo one), so two clients
 * never race for the claim on the same standing choice.
 */
function standingArmed(flow, { defenceKey, standing, sessionId }) {
  if (!defenceKey || !standing) return false;
  // A chain under way or a roll in flight has already answered the prompt.
  if (flow.defenceChain || flow.defenceResolving) return false;
  return !sessionId || flow.reactionVolume === 'loud';
}

/** The bars that ask one question mid-attack, in the order they stack. */
function PromptStack({ flow, defencePrompt }) {
  const { pendingTurnEffect, pendingBonusAttack, pendingOverheat, pendingReroll, defenceChain } = flow;
  const { roster, actorId } = useRosterContext();
  const owner = (prompt) => promptOwner(prompt, actorId, roster);
  return (
    <>
      {pendingTurnEffect && <TurnEffectPrompt effect={pendingTurnEffect} onChoice={flow.handleTurnEffectChoice} />}
      {pendingBonusAttack && (
        <BonusAttackPrompt owner={owner(pendingBonusAttack)} onChoice={flow.handleBonusAttackChoice} />
      )}
      {pendingOverheat && <OverheatPrompt overheat={pendingOverheat} onChoice={flow.handleOverheatChoice} />}
      {pendingReroll && (
        <RerollPrompt reroll={pendingReroll} owner={owner(pendingReroll)} onChoice={flow.handleRerollChoice} />
      )}
      {defenceChain && (
        <DefenceChainBar
          chain={defenceChain}
          onStep={flow.handleDefenceStep}
          onAcceptRemaining={flow.acceptRemainingHits}
        />
      )}
      {flow.reactionsAwaiting?.length > 0 && <AwaitingBar awaiting={flow.reactionsAwaiting} />}
      {/* Initial defence prompt - no chain active yet */}
      {!defenceChain && defencePrompt}
    </>
  );
}

/**
 * THE BOTTOM BAR'S FOUR ON THE LOG'S EDGE (2026-10-22, plan retire-classic): on the rails the log is a ticker, rolled
 * up most of the time, and the foot that held the bar went with it. The edge is the one log surface always there -
 * rolled up, open or folded - so the four live on it, in short words (the canvas's A), and Call a test's dialog no
 * longer unmounts when the log rolls up.
 */
function EdgeActions({ flow, isLocked, doResetTarget, callTestNode }) {
  const resetLocked = isLocked('resetTarget');
  return (
    <div className={styles.edgeActions} data-testid="log-edge-actions">
      <button type="button" className={common.btnSmall} data-testid="log-clear" onClick={flow.doClearNarrative}>
        Clear (C)
      </button>
      <button
        type="button"
        className={clsx(common.btnSmall, resetLocked && styles.locked)}
        data-testid="log-reset-target"
        title={resetLocked ? 'Locked by the GM' : 'Reset the target (T)'}
        onClick={doResetTarget}
      >
        {resetLocked ? 'Reset target (locked)' : 'Reset target (T)'}
      </button>
      {callTestNode}
      <button type="button" className={common.btnSmall} data-testid="log-copy" onClick={flow.doCopyNarrative}>
        Copy
      </button>
    </div>
  );
}

/**
 * Props are the log's curtain (logView, logHeight, collapsed, logResizeHandlers, narrativeRef), the log filter, the
 * order strip and battlefield nodes, with `turnCardNode` and `stealthStripNode` laid over the map (plan design-gaps
 * L2 and L5), `onLookAt` (click a ping's entry to point your camera at it), and the story's nodes: `storyAsksNode`
 * (what waits on an answer, after the prompts), `storyNode` (the story's other bars), `callTestNode` (the director's
 * button, on the log's edge) and `onPinLine` (pin a vox line to the board). Everything else comes from the contexts.
 */
function CombatLogPanel(props) {
  const { collapsed, orderRailNode } = props;
  const { activeTab, currentTab, doResetTarget } = useTargets();
  const { isGm, isLocked, sessionId, permissions } = useSession();
  const vox = useChatContext();
  const flow = useCombatFlow();
  const defence = currentDefence(flow, activeTab);
  const { manualMod, setManualMod } = useDefenceRulings(defence);
  // The view switch only appears once there is a session: solo play has
  // nothing to share, and a lone player must not lose their own log to a
  // tab they were never told about.
  const view = resolveLogView(props.logView, sessionId);
  useVoxReadOnView(vox, collapsed, view);
  const derived = deriveDefence({ defence, currentTab, permissions, standingReaction: flow.standingReaction });
  const defenceKey = defenceKeyOf(defence);
  const auto = useStandingAuto({
    armed: standingArmed(flow, { defenceKey, standing: derived.standing, sessionId }),
    defenceKey,
    standing: derived.standing,
    handleDefenceChoice: flow.handleDefenceChoice,
    pendingDefence: flow.pendingDefence,
    manualMod,
  });
  const defencePrompt = defence && (
    <DefencePrompt
      {...flow}
      defence={defence}
      derived={derived}
      auto={auto}
      isGm={isGm}
      {...{ manualMod, setManualMod }}
    />
  );

  // THE ORDER OVER THE MAP, owned here (2026-10-22): the stack is capped at a third of the map (CappedBars), so what
  // needs an answer comes first - the attack's prompts, then the story's asks (a test, a reveal, a disposition) -
  // and the story's other bars after; nothing that waits on a hand is ever the bar below the fold.
  const bars = (
    <>
      <PromptStack flow={flow} defencePrompt={defencePrompt} />
      {props.storyAsksNode}
      {props.storyNode}
    </>
  );
  const actions = { flow, isLocked, doResetTarget, callTestNode: props.callTestNode };
  // THE MAP HAS THE COLUMN (plan design-gaps, L4): the order is a strip above it, the bars lie over its top edge, and
  // the bottom bar's four sit on the log's edge (2026-10-22).
  return (
    <div className={styles.centreMain}>
      {orderRailNode}
      <CombatLogOverlay
        {...props}
        view={view}
        vox={vox}
        sessionId={sessionId}
        currentTab={currentTab}
        firestoreNarrative={flow.firestoreNarrative}
        topBarsNode={bars}
        edgeActionsNode={<EdgeActions {...actions} />}
      />
    </div>
  );
}

export default memo(CombatLogPanel);
